import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { BottomSheet } from "../../components/ui/bottom-sheet";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Pencil, Save, X, KeyRound, Phone, Mail, User as UserIcon, ShoppingBag, Flame, Droplets, ArrowRight, Bell, BellOff } from "lucide-react";
import { getPushStatus, enablePush, disablePush, sendTestPush } from "../../lib/push";

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [saving, setSaving] = useState(false);

  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdCurrent, setPwdCurrent] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdConfirm, setPwdConfirm] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);

  // BottomSheet "Empezar pedido" — espejo del comportamiento del Home
  // (hero section). Permite que el cliente arranque un pedido desde
  // su perfil sin tener que volver al Home, eligiendo el canal (Gas
  // o Cilindros) antes de entrar al wizard.
  const [chooseOpen, setChooseOpen] = useState(false);

  // Estado de notificaciones push — se carga al montar la vista.
  const [pushStatus, setPushStatus] = useState({ supported: false, permission: "default", subscribed: false });
  const [pushBusy, setPushBusy] = useState(false);

  // Email notifications toggle — viene en `user.email_notifications`
  // (default true). Al cambiar, sincroniza con backend `PATCH /auth/me`.
  const [emailNotif, setEmailNotif] = useState(user?.email_notifications !== false);
  const [emailBusy, setEmailBusy] = useState(false);
  useEffect(() => {
    setEmailNotif(user?.email_notifications !== false);
  }, [user?.email_notifications]);

  const toggleEmailNotif = async () => {
    const next = !emailNotif;
    setEmailBusy(true);
    setEmailNotif(next);
    try {
      const { data } = await api.patch("/auth/me", { email_notifications: next });
      setUser(data);
      toast.success(next ? "Notificaciones por email activadas" : "Notificaciones por email desactivadas");
    } catch (e) {
      setEmailNotif(!next);
      toast.error(formatApiError(e));
    } finally {
      setEmailBusy(false);
    }
  };

  const sendTestEmail = async () => {
    setEmailBusy(true);
    try {
      const { data } = await api.post("/auth/test-email");
      if (data.sent) toast.success(`Email de prueba enviado a ${user.email}`);
      else toast.error("No se pudo enviar el email de prueba.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEmailBusy(false);
    }
  };
  useEffect(() => {
    getPushStatus().then(setPushStatus);
  }, []);

  const handleEnablePush = async () => {
    setPushBusy(true);
    try {
      const res = await enablePush();
      if (res.ok) {
        toast.success("Notificaciones activadas. Te avisaremos cuando tu pedido cambie de estado.");
        setPushStatus(await getPushStatus());
      } else if (res.reason === "denied") {
        toast.error("Permiso denegado. Activa las notificaciones desde los ajustes del navegador.");
      } else if (res.reason === "unsupported") {
        toast.error("Tu navegador no soporta notificaciones push.");
      } else {
        toast.error("No se pudo activar las notificaciones.");
      }
    } finally {
      setPushBusy(false);
    }
  };

  const handleDisablePush = async () => {
    setPushBusy(true);
    try {
      await disablePush();
      toast.success("Notificaciones desactivadas.");
      setPushStatus(await getPushStatus());
    } finally {
      setPushBusy(false);
    }
  };

  const handleTestPush = async () => {
    const ok = await sendTestPush();
    if (ok) toast.success("Push de prueba enviado — revisa tu bandeja del sistema.");
    else toast.error("No se pudo enviar el push de prueba.");
  };

  const startEdit = () => {
    setName(user?.name || "");
    setPhone(user?.phone || "");
    setEditing(true);
  };
  const cancelEdit = () => setEditing(false);

  const save = async () => {
    if (!name.trim()) { toast.error("El nombre es requerido"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me", {
        name: name.trim(),
        phone: phone.trim() || null,
      });
      setUser(data);
      setEditing(false);
      toast.success("Datos actualizados");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (pwdNew.length < 8) { toast.error("Mínimo 8 caracteres"); return; }
    if (pwdNew !== pwdConfirm) { toast.error("Las contraseñas no coinciden"); return; }
    setPwdSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwdCurrent,
        new_password: pwdNew,
      });
      toast.success("Contraseña actualizada");
      setPwdOpen(false);
      setPwdCurrent(""); setPwdNew(""); setPwdConfirm("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo actualizar");
    } finally {
      setPwdSaving(false);
    }
  };

  const initials = (user?.name || user?.email || "?").charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-neutral-50">
      <main className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        <h1 className="font-display text-4xl font-extrabold tracking-tight">Mi perfil</h1>

        <div className="mt-6 bg-white border border-neutral-200 rounded-2xl p-6 space-y-4" data-testid="profile-card">
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-14 w-14 rounded-full" />
            ) : (
              <div className="h-14 w-14 rounded-full bg-company text-[color:var(--company-primary-contrast)] flex items-center justify-center font-bold text-lg">
                {initials}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-display font-extrabold text-2xl truncate">{user?.name || "—"}</p>
              <p className="text-sm text-neutral-500 truncate">{user?.email}</p>
            </div>
            {!editing && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={startEdit}
                className="shrink-0"
                data-testid="edit-profile-btn"
              >
                <Pencil size={14} className="mr-1.5" />
                Editar
              </Button>
            )}
          </div>

          {!editing ? (
            <div className="space-y-2 pt-2 border-t border-neutral-100">
              <div className="flex items-center gap-2 text-sm">
                <Mail size={14} className="text-neutral-400 shrink-0" />
                <span className="text-neutral-400">Correo:</span>
                <span className="font-medium">{user?.email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone size={14} className="text-neutral-400 shrink-0" />
                <span className="text-neutral-400">Teléfono:</span>
                <span className="font-medium">{user?.phone || "—"}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <UserIcon size={14} className="text-neutral-400 shrink-0" />
                <span className="text-neutral-400">Rol:</span>
                <span className="font-medium capitalize">{user?.role?.replace("_", " ")}</span>
              </div>
            </div>
          ) : (
            <div className="space-y-3 pt-2 border-t border-neutral-100">
              <div>
                <Label>Nombre completo</Label>
                <Input
                  className="h-11 mt-1"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nombre Apellido"
                  data-testid="profile-name-input"
                />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  className="h-11 mt-1"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+57 300 000 0000"
                  data-testid="profile-phone-input"
                />
                <p className="text-xs text-neutral-500 mt-1">Este número se usa para avisos por WhatsApp y seguimiento de tu pedido.</p>
              </div>
              <div>
                <Label>Correo</Label>
                <Input className="h-11 mt-1" value={user?.email || ""} disabled />
                <p className="text-xs text-neutral-500 mt-1">El correo es tu identificador de cuenta y no se puede cambiar aquí.</p>
              </div>
              <div className="flex gap-2 pt-1">
                <Button type="button" variant="outline" onClick={cancelEdit} disabled={saving} className="flex-1 h-11" data-testid="profile-cancel-btn">
                  <X size={14} className="mr-1.5" />Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  className="flex-1 h-11 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90"
                  data-testid="profile-save-btn"
                >
                  <Save size={14} className="mr-1.5" />
                  {saving ? "Guardando…" : "Guardar"}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Atajo "Empezar pedido" — mismo flujo que el Home: abre un
            BottomSheet con la elección Gas / Cilindros y navega al
            wizard ya filtrado por canal. Pensado para clientes que
            están viendo su perfil y deciden ordenar sin volver al
            Home. */}
        <Button
          type="button"
          size="lg"
          onClick={() => setChooseOpen(true)}
          className="mt-4 w-full h-12 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90 font-semibold"
          data-testid="profile-start-order-btn"
        >
          <ShoppingBag size={16} className="mr-2" />
          Empezar pedido
        </Button>

        {/* Notificaciones push — sólo se muestra si el navegador las
            soporta. Estado visible: "off / subscribed / denied". */}
        {pushStatus.supported && (
          <div
            className="mt-4 bg-white border border-neutral-200 rounded-2xl p-5"
            data-testid="push-section"
          >
            <div className="flex items-start gap-3">
              <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
                pushStatus.subscribed ? "bg-green-100 text-green-700" : "bg-neutral-100 text-neutral-500"
              }`}>
                {pushStatus.subscribed ? <Bell size={18} /> : <BellOff size={18} />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-base">Notificaciones del pedido</p>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {pushStatus.subscribed
                    ? "Recibes avisos cuando tu pedido es confirmado, sale y llega."
                    : "Activa avisos para saber cuándo llega tu gas."}
                </p>
                {pushStatus.permission === "denied" && (
                  <p className="text-xs text-red-600 mt-1">
                    Permiso bloqueado en el navegador. Abre ajustes del sitio y permite notificaciones.
                  </p>
                )}
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              {!pushStatus.subscribed ? (
                <Button
                  type="button"
                  onClick={handleEnablePush}
                  disabled={pushBusy || pushStatus.permission === "denied"}
                  className="flex-1 h-10 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90"
                  data-testid="push-enable-btn"
                >
                  <Bell size={14} className="mr-1.5" />
                  {pushBusy ? "Activando…" : "Activar notificaciones"}
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleTestPush}
                    className="flex-1 h-10"
                    data-testid="push-test-btn"
                  >
                    Enviar prueba
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDisablePush}
                    disabled={pushBusy}
                    className="flex-1 h-10 text-red-600 hover:bg-red-50 border-red-200"
                    data-testid="push-disable-btn"
                  >
                    <BellOff size={14} className="mr-1.5" />
                    Desactivar
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Notificaciones por email — misma estructura visual que la
            tarjeta de push para que ambas se vean consistentes. */}
        <div className="mt-3 bg-white border border-neutral-200 rounded-2xl p-5" data-testid="email-section">
          <div className="flex items-start gap-3">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
              emailNotif ? "bg-blue-100 text-blue-700" : "bg-neutral-100 text-neutral-500"
            }`}>
              <Mail size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-base">Notificaciones por email</p>
              <p className="text-xs text-neutral-500 mt-0.5">
                {emailNotif
                  ? `Te enviaremos un correo a ${user.email} en cada cambio de estado.`
                  : "Activa los avisos por correo cuando tu pedido cambie de estado."}
              </p>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            {!emailNotif ? (
              <Button
                type="button"
                onClick={toggleEmailNotif}
                disabled={emailBusy}
                className="flex-1 h-10 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90"
                data-testid="email-enable-btn"
              >
                <Mail size={14} className="mr-1.5" />
                {emailBusy ? "Activando…" : "Activar correos"}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={sendTestEmail}
                  disabled={emailBusy}
                  className="flex-1 h-10"
                  data-testid="email-test-btn"
                >
                  Enviar prueba
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={toggleEmailNotif}
                  disabled={emailBusy}
                  className="flex-1 h-10 text-red-600 hover:bg-red-50 border-red-200"
                  data-testid="email-disable-btn"
                >
                  <X size={14} className="mr-1.5" />
                  Desactivar
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Password section — hidden for Google-only accounts (no password set). */}
        <div className="mt-4 bg-white border border-neutral-200 rounded-2xl p-6">
          {!pwdOpen ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => setPwdOpen(true)}
              className="w-full h-11 justify-start"
              data-testid="change-password-btn"
            >
              <KeyRound size={14} className="mr-2" />Cambiar contraseña
            </Button>
          ) : (
            <div className="space-y-3">
              <h3 className="font-display font-bold">Cambiar contraseña</h3>
              <div>
                <Label>Contraseña actual</Label>
                <Input
                  type="password"
                  className="h-11 mt-1"
                  value={pwdCurrent}
                  onChange={(e) => setPwdCurrent(e.target.value)}
                  data-testid="pwd-current-input"
                />
              </div>
              <div>
                <Label>Nueva contraseña</Label>
                <Input
                  type="password"
                  className="h-11 mt-1"
                  value={pwdNew}
                  onChange={(e) => setPwdNew(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  data-testid="pwd-new-input"
                />
              </div>
              <div>
                <Label>Confirmar nueva contraseña</Label>
                <Input
                  type="password"
                  className="h-11 mt-1"
                  value={pwdConfirm}
                  onChange={(e) => setPwdConfirm(e.target.value)}
                  data-testid="pwd-confirm-input"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setPwdOpen(false)} disabled={pwdSaving} className="flex-1 h-11">
                  <X size={14} className="mr-1.5" />Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={changePassword}
                  disabled={pwdSaving || !pwdCurrent || !pwdNew || !pwdConfirm}
                  className="flex-1 h-11 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90"
                  data-testid="pwd-save-btn"
                >
                  {pwdSaving ? "Guardando…" : "Actualizar contraseña"}
                </Button>
              </div>
            </div>
          )}
        </div>

        <Button
          onClick={async () => { await logout(); navigate("/"); }}
          variant="outline"
          className="mt-4 w-full h-11"
          data-testid="logout-btn"
        >
          Cerrar sesión
        </Button>

        {/* BottomSheet espejo del Home para el botón "Empezar pedido". */}
        <BottomSheet
          open={chooseOpen}
          onOpenChange={setChooseOpen}
          title="¿Qué vas a pedir?"
          description="Elige el tipo de producto para continuar."
          testid="profile-start-order-sheet"
        >
          <div className="px-6 pb-6">
            <div className="mt-2 grid grid-cols-2 gap-3 max-w-2xl mx-auto">
              <button
                type="button"
                onClick={() => { setChooseOpen(false); navigate("/app/order/new?kind=bulk&fresh=1"); }}
                className="group rounded-2xl border border-neutral-200 p-5 text-left hover:border-company hover:shadow-md transition-all bg-white"
                data-testid="profile-sheet-choose-bulk"
              >
                <Droplets size={26} className="text-company" />
                <p className="mt-3 font-display font-extrabold text-xl">Gas</p>
                <p className="text-xs text-neutral-500 mt-0.5">Litros o kilogramos</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold">
                  Elegir <ArrowRight size={14} />
                </span>
              </button>
              <button
                type="button"
                onClick={() => { setChooseOpen(false); navigate("/app/order/new?kind=cylinder&fresh=1"); }}
                className="group rounded-2xl border border-neutral-200 p-5 text-left hover:border-company hover:shadow-md transition-all bg-white"
                data-testid="profile-sheet-choose-cylinder"
              >
                <Flame size={26} className="text-company" />
                <p className="mt-3 font-display font-extrabold text-xl">Cilindros</p>
                <p className="text-xs text-neutral-500 mt-0.5">20 / 35 / 45 kg</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold">
                  Elegir <ArrowRight size={14} />
                </span>
              </button>
            </div>
          </div>
        </BottomSheet>
      </main>
    </div>
  );
}
